import { useEffect, useState } from 'react';
import { collection, query, onSnapshot, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface KnowledgeDocument {
  id: string;
  documentId: string;
  knowledgeId: string;
  userId: string;
  filename: string;
  fileUrl: string;
  storagePath: string | null;
  contentType: string;
  type: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  embeddingEnabled?: boolean;
  embeddingSucceeded?: boolean;
  chunksCreated?: number;
  embeddingError?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export function useKnowledgeDocuments(knowledgeId: string | undefined) {
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!knowledgeId) {
      setLoading(false);
      return;
    }

    const documentsRef = collection(db, 'knowledge', knowledgeId, 'documents');
    const q = query(documentsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs: KnowledgeDocument[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          docs.push({
            id: docSnap.id,
            documentId: data.documentId || docSnap.id,
            knowledgeId: data.knowledgeId || knowledgeId,
            userId: data.userId || '',
            filename: data.filename || 'Unknown',
            fileUrl: data.fileUrl || '',
            storagePath: data.storagePath || null,
            contentType: data.contentType || 'application/pdf',
            type: data.type || 'knowledge_document',
            status: data.status || 'processing',
            embeddingEnabled: data.embeddingEnabled,
            embeddingSucceeded: data.embeddingSucceeded,
            chunksCreated: data.chunksCreated,
            embeddingError: data.embeddingError ?? null,
            createdAt:
              data.createdAt instanceof Timestamp
                ? data.createdAt.toDate()
                : new Date(),
            updatedAt:
              data.updatedAt instanceof Timestamp
                ? data.updatedAt.toDate()
                : new Date(),
          });
        });

        setDocuments(docs);
        setLoading(false);
      },
      (error) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching knowledge documents:', error);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [knowledgeId]);

  return { documents, loading };
}
